import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Resend-OTP countdown. Replaces the hand-rolled setInterval blocks in TwoFA
 * and VerifyMail, which re-created their interval on every tick (both `seconds`
 * and `minutes` were in the dependency array).
 */
export const useCountdown = (initialSeconds: number) => {
  const [remaining, setRemaining] = useState<number>(initialSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clear = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const start = useCallback((seconds: number = initialSeconds) => {
    setRemaining(seconds);
  }, [initialSeconds]);

  useEffect(() => {
    if (remaining <= 0) {
      clear();
      return;
    }
    intervalRef.current = setInterval(() => {
      setRemaining((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);

    return clear;
    // Only re-arm when the timer transitions between "running" and "expired",
    // not on every tick.
  }, [remaining > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;

  return {
    remaining,
    isRunning: remaining > 0,
    label: `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`,
    restart: start,
  };
};
